"use client"

import { useState, useCallback, useRef, useEffect, useMemo } from "react"
import { motion } from "framer-motion"
import { X, Save, Database } from "lucide-react"
import { Button } from "../../../components/ui/button"
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from "reactflow"
import "reactflow/dist/style.css"
import "../../../../ApiCalls/mantisFlowsManangment";

// Importar los tipos de nodos personalizados
import DocumentNode from "../../FlowCreator/FlowVisualization/nodes/DocumentNode"
import ProcessNode from "../../FlowCreator/FlowVisualization/nodes/ProcessNode"
import StorageNode from "../../FlowCreator/FlowVisualization/nodes/StorageNode"
import ActionNode from "../../FlowCreator/FlowVisualization/nodes/ActionNode"
import NotificationNode from "../../FlowCreator/FlowVisualization/nodes/NotificationNode";
import OutputNode from "../../FlowCreator/FlowVisualization/nodes/OutputNode";

// Registrar los tipos de nodos personalizados
const nodeTypes = {
  documentNode: DocumentNode,
  documentProcessor: DocumentNode,
  processNode: ProcessNode,
  flowUnderstandingAgent: ProcessNode,
  logicAgent: ProcessNode,
  qualityChecker: ProcessNode,
  storageNode: StorageNode,
  dataAdministrator: StorageNode,
  actionNode: ActionNode,
  channelManager: ActionNode,
  accessPermissionManager: ActionNode,
  outputNode: OutputNode,
  decision: OutputNode,
  inputOutput: OutputNode,
  loopsCycles: OutputNode,
  notificationNode: NotificationNode,
};

// Nodos de ejemplo para demostración
const demoNodes = [
  {
    id: "1",
    type: "documentNode",
    data: { label: "Datos Financieros" },
    position: { x: 100, y: 100 },
  },
  {
    id: "2",
    type: "processNode",
    data: { label: "Análisis Preliminar" },
    position: { x: 300, y: 100 },
  },
  {
    id: "3",
    type: "actionNode",
    data: { label: "Cálculo de Métricas" },
    position: { x: 500, y: 100 },
  },
  {
    id: "4",
    type: "storageNode",
    data: { label: "Almacenamiento Temporal" },
    position: { x: 300, y: 250 },
  },
  {
    id: "5",
    type: "outputNode",
    data: { label: "Reporte Financiero" },
    position: { x: 700, y: 100 },
  },
];

// Conexiones de ejemplo
const demoEdges = [
  {
    id: "e1-2",
    source: "1",
    target: "2",
    type: "smoothstep",
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: "#f97316", strokeWidth: 2 },
  },
  {
    id: "e2-3",
    source: "2",
    target: "3",
    type: "smoothstep",
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: "#f97316", strokeWidth: 2 },
  },
  {
    id: "e3-5",
    source: "3",
    target: "5",
    type: "smoothstep",
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: "#f97316", strokeWidth: 2 },
  },
  {
    id: "e2-4",
    source: "2",
    target: "4",
    type: "smoothstep",
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: "#f97316", strokeWidth: 2 },
  },
];

export default function FlowEditWorkflow({ flowData, isOpen, onClose }) {
  const reactFlowWrapper = useRef(null)
  const [, setReactFlowInstance] = useState(null)
  // Inicializar con nodos y bordes del flujo o usar los de demostración
  const [nodes, setNodes, onNodesChange] = useNodesState(
    flowData?.nodes?.length > 0 ? flowData.nodes : demoNodes
  )
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    flowData?.edges?.length > 0 ? flowData.edges : demoEdges
  )

  // Usar las columnas que vienen directamente de flowData
  const columns = useMemo(() => flowData?.columns || [], [flowData?.columns])
  const visibleColumns = useMemo(
    () => flowData?.visibleColumns || [],
    [flowData?.visibleColumns]
  )

  // Log para verificar los datos recibidos
  useEffect(() => {
    console.log("FlowEditWorkflow - Datos recibidos:", {
      columns,
      visibleColumns,
      flowData
    });
  }, [columns, visibleColumns, flowData]);

  // Update the useEffect to reset nodes and edges when flowData changes
  useEffect(() => {
    if (flowData?.nodes?.length > 0) {
      setNodes(flowData.nodes);
    }
    
    if (flowData?.edges?.length > 0) {
      setEdges(flowData.edges);
    }
  }, [flowData, setNodes, setEdges]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  )

  const onInit = useCallback(
    (instance) => {
      setReactFlowInstance(instance)
    },
    []
  )

  const handleSaveFlow = () => {
    // Aquí iría la lógica para guardar el flujo
    console.log("Guardando flujo:", { nodes, edges })
    // Mostrar notificación o feedback al usuario
  }

  // Formatear nombre de columna para mostrar
  const formatColumnName = (columnName) => {
    const words = columnName.split('_');
    return words
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Verificar si una columna está en uso en el flujo
  const isColumnInUse = (column) => {
    return visibleColumns.includes(column);
  }

  return (
    <motion.div
      className="flex flex-col h-full bg-white dark:bg-slate-800 rounded overflow-hidden shadow-xl border border-slate-200 dark:border-slate-700"
    >
      {/* Header */}
      <div className="p-3 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-blue-50 to-white dark:from-blue-900/30 dark:to-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center">
            <img 
              src="/mantisWhiteIsotype.png" 
              alt="Mantis Logo" 
              className="h-4 w-4 object-contain"
            />
          </div>
          <h3 className="font-medium text-slate-800 dark:text-slate-200">
            Editor de Flujo
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveFlow}
            className="h-7 flex items-center gap-1 text-blue-500 hover:bg-blue-50 dark:hover:bg-slate-700 border-blue-200 dark:border-blue-800"
          >
            <Save className="h-3.5 w-3.5" />
            <span>Guardar</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-7 w-7 p-0 rounded-full text-blue-500 hover:bg-blue-100 dark:hover:bg-slate-600"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Flow Canvas */}
      <div className="flex-1 w-full h-full" ref={reactFlowWrapper}>
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={onInit}
            nodeTypes={nodeTypes}
            fitView
            attributionPosition="bottom-right"
            className="bg-slate-50 dark:bg-slate-900"
            deleteKeyCode="Delete"
            minZoom={0.2}
            maxZoom={4}
            defaultEdgeOptions={{
              type: 'smoothstep',
              style: { stroke: '#f97316', strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed }
            }}
            connectionLineStyle={{ stroke: '#f97316', strokeWidth: 2 }}
            connectionLineType="smoothstep"
            // Desactivamos la interacción manual con los nodos
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
          >
            <Background color="#f97316" gap={16} size={1} />
            <Controls showInteractive={false} />
            <MiniMap
              nodeStrokeColor={(n) => {
                if (n.type === 'documentNode' || n.type === 'documentProcessor') return '#3b82f6';
                if (n.type === 'processNode' || n.type === 'flowUnderstandingAgent' || n.type === 'logicAgent' || n.type === 'qualityChecker') return '#8b5cf6';
                if (n.type === 'storageNode' || n.type === 'dataAdministrator') return '#10b981';
                if (n.type === 'actionNode' || n.type === 'channelManager' || n.type === 'accessPermissionMaanager') return '#f59e0b';
                if (n.type === 'outputNode' || n.type === 'decision' || n.type === 'inputOutput' || n.type === 'loopsCycles') return '#ef4444'; 
                if (n.type === 'notificationNode') return '#e11d48';
                return '#6b7280';
              }}
              nodeColor={(n) => {
                if (n.type === 'documentNode' || n.type === 'documentProcessor') return '#dbeafe';
                if (n.type === 'processNode' || n.type === 'flowUnderstandingAgent' || n.type === 'logicAgent' || n.type === 'qualityChecker') return '#ede9fe';
                if (n.type === 'storageNode' || n.type === 'dataAdministrator') return '#d1fae5';
                if (n.type === 'actionNode' || n.type === 'channelManager' || n.type === 'accessPermissionMaanager') return '#fef3c7';
                if (n.type === 'outputNode' || n.type === 'decision' || n.type === 'inputOutput' || n.type === 'loopsCycles') return '#fee2e2'; 
                if (n.type === 'notificationNode') return '#fee2e2';
                return '#f3f4f6';
              }}
              nodeBorderRadius={2}
            />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
      
      {/* Footer con columnas disponibles - siempre visible */}
      <div className="border-t border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/50">
        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
          Campos disponibles
        </h4>
        <div className="flex flex-wrap gap-2">
          {columns.map((column) => (
            <div
              key={column}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 flex items-center ${
                isColumnInUse(column)
                  ? "bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60"
                  : "bg-white text-slate-600 border border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600"
              }`}
            >
              <Database className={`h-3 w-3 mr-1 ${
                isColumnInUse(column) 
                  ? "text-blue-500 dark:text-blue-400" 
                  : "text-slate-400 dark:text-slate-500"
              }`} />
              {formatColumnName(column)}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}
